<template>
  <div class="receipt-settings-page">
    <div class="receipt-page-head">
      <div>
        <h2 class="receipt-page-title">小票模板</h2>
        <p class="receipt-page-desc">
          映美云用于随心订发货小票；飞鹅云用于非随心订进单出票。两套模板独立配置、互不影响。
        </p>
      </div>
      <div class="header-actions">
        <el-button class="btn-secondary" @click="openPreview">预览{{ printerLabel }}</el-button>
        <el-button class="btn-primary" :loading="saving" @click="saveConfig">保存{{ printerLabel }}</el-button>
      </div>
    </div>

    <el-tabs v-model="activePrinter" class="printer-tabs" @tab-click="onPrinterTab">
      <el-tab-pane label="映美云小票" name="jolimark" />
      <el-tab-pane label="飞鹅云小票" name="feie" />
    </el-tabs>

    <section class="receipt-panel print-switch-panel">
      <header class="panel-head">
        <h3>打印开关</h3>
        <p>
          {{ activePrinter === 'feie' ? '仅控制飞鹅云' : '仅控制映美云' }}；两套互不影响。关闭后不会发到对应打印机，预览仍可用。
        </p>
      </header>
      <div class="print-switch-row">
        <template v-if="activePrinter === 'jolimark'">
          <el-switch
            v-model="jolimarkForm.print_enabled"
            active-text="映美云打印已开启"
            inactive-text="映美云打印已关闭"
            :disabled="printSwitchSaving"
            @change="savePrintEnabled('jolimark')"
          />
          <span class="print-switch-hint">含随心订「下单成功」自动出票与发货/核销小票</span>
        </template>
        <template v-else>
          <el-switch
            v-model="feieForm.print_enabled"
            active-text="飞鹅云打印已开启"
            inactive-text="飞鹅云打印已关闭"
            :disabled="printSwitchSaving"
            @change="savePrintEnabled('feie')"
          />
          <span class="print-switch-hint">含非随心订进单自动出单与管理端飞鹅补打</span>
        </template>
      </div>
    </section>

    <el-alert
      v-if="activePrinter === 'jolimark'"
      type="info"
      :closable="false"
      show-icon
      title="映美云小票（MCP-58）"
      :description="jolimarkStatusText"
      class="receipt-alert"
    />
    <el-alert
      v-else
      type="success"
      :closable="false"
      show-icon
      title="飞鹅云小票（FP-V58-WB）"
      :description="feieStatusText"
      class="receipt-alert"
    />

    <!-- 映美云 -->
    <template v-if="activePrinter === 'jolimark'">
      <div class="receipt-flat-grid">
        <section class="receipt-panel">
          <header class="panel-head"><h3>基础信息</h3></header>
          <el-form label-width="96px" size="small" class="receipt-form">
            <el-form-item label="小票标题">
              <el-input v-model="jolimarkForm.title" maxlength="20" placeholder="发货小票" />
            </el-form-item>
            <el-form-item label="店铺电话">
              <el-input v-model="jolimarkForm.store_phone" maxlength="30" />
            </el-form-item>
            <el-form-item label="店铺地址">
              <el-input v-model="jolimarkForm.store_address" type="textarea" :rows="3" maxlength="200" />
            </el-form-item>
            <el-form-item label="客服码说明">
              <el-input v-model="jolimarkForm.service_qr_label" maxlength="12" />
            </el-form-item>
            <el-form-item label="小程序码说明">
              <el-input v-model="jolimarkForm.mini_program_qr_label" maxlength="12" />
            </el-form-item>
          </el-form>
        </section>

        <section class="receipt-panel">
          <header class="panel-head">
            <h3>图片资源</h3>
            <p>Logo 与二维码平铺展示，点击更换。</p>
          </header>
          <div class="upload-grid">
            <div v-for="item in uploadItems" :key="item.kind" class="upload-card">
              <div class="upload-title">{{ item.label }}</div>
              <div class="upload-preview">
                <img v-if="assetUrl(item.pathKey)" :src="assetUrl(item.pathKey)" :alt="item.label" />
                <span v-else class="upload-empty">未上传</span>
              </div>
              <el-upload
                action="#"
                :show-file-list="false"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                :http-request="(opt) => uploadAsset(opt, item.kind)"
              >
                <el-button size="mini" class="btn-secondary">更换图片</el-button>
              </el-upload>
            </div>
          </div>
        </section>
      </div>

      <section class="receipt-panel receipt-panel-full">
        <header class="panel-head">
          <h3>字段显示与标签</h3>
          <p class="section-hint">可关闭不需要的字段，或修改小票上显示的标签文字。</p>
        </header>
        <el-table :data="jolimarkFieldRows" size="small" border class="fields-table">
          <el-table-column prop="labelZh" label="字段" width="160" />
          <el-table-column label="显示" width="100" align="center">
            <template slot-scope="scope">
              <el-switch v-model="jolimarkForm.fields[scope.row.key].enabled" />
            </template>
          </el-table-column>
          <el-table-column label="小票标签">
            <template slot-scope="scope">
              <el-input v-model="jolimarkForm.fields[scope.row.key].label" size="mini" maxlength="16" />
            </template>
          </el-table-column>
        </el-table>
      </section>
    </template>

    <!-- 飞鹅云 -->
    <template v-else>
      <div class="receipt-flat-grid">
        <section class="receipt-panel">
          <header class="panel-head"><h3>基础信息</h3></header>
          <el-form label-width="96px" size="small" class="receipt-form">
            <el-form-item label="小票标题">
              <el-input v-model="feieForm.title" maxlength="12" placeholder="新订单" />
            </el-form-item>
            <el-form-item label="店铺电话">
              <el-input v-model="feieForm.store_phone" maxlength="30" />
            </el-form-item>
            <el-form-item label="店铺地址">
              <el-input v-model="feieForm.store_address" type="textarea" :rows="3" maxlength="200" />
            </el-form-item>
            <el-form-item label="打印规格">
              <el-switch v-model="feieForm.show_specs" active-text="显示规格明细" inactive-text="隐藏规格" />
            </el-form-item>
          </el-form>
        </section>
        <section class="receipt-panel">
          <header class="panel-head">
            <h3>适用场景</h3>
          </header>
          <ul class="feie-tips">
            <li>超级碗 / 饮品 / 小食等<strong>非随心订</strong>订单</li>
            <li>小程序支付成功、0 元券抵扣、收银台收款后<strong>自动出单</strong></li>
            <li>管理端「小票打印」页可手动补打飞鹅云</li>
            <li>58mm 热敏排版，不支持 Logo / 二维码图片</li>
          </ul>
        </section>
      </div>

      <section class="receipt-panel receipt-panel-full">
        <header class="panel-head">
          <h3>字段显示与标签</h3>
          <p class="section-hint">控制飞鹅出单内容；关闭后进单小票也不再打印该字段。</p>
        </header>
        <el-table :data="feieFieldRows" size="small" border class="fields-table">
          <el-table-column prop="labelZh" label="字段" width="160" />
          <el-table-column label="显示" width="100" align="center">
            <template slot-scope="scope">
              <el-switch v-model="feieForm.fields[scope.row.key].enabled" />
            </template>
          </el-table-column>
          <el-table-column label="小票标签">
            <template slot-scope="scope">
              <el-input v-model="feieForm.fields[scope.row.key].label" size="mini" maxlength="16" />
            </template>
          </el-table-column>
        </el-table>
      </section>
    </template>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'
import { getAdminToken } from '../utils/auth'
import { RECEIPT_FIELD_META, FEIE_FIELD_META } from '../constants/receiptFieldMeta'

function defaultFields(meta) {
  const fields = {}
  for (const f of meta) {
    fields[f.key] = { label: f.labelZh, enabled: true }
  }
  return fields
}

export default {
  name: 'ReceiptSettings',
  data() {
    return {
      activePrinter: 'jolimark',
      saving: false,
      printSwitchSaving: false,
      printerStatus: null,
      jolimarkForm: {
        title: '发货小票',
        print_enabled: true,
        logo_path: '/api/receipt-assets/eating-logo-receipt.png',
        service_qr_path: '/api/receipt-assets/service-qr.png',
        mini_program_qr_path: '/api/receipt-assets/mini-program-qr.png',
        service_qr_label: '店铺客服',
        mini_program_qr_label: '点餐小程序',
        store_phone: '',
        store_address: '',
        fields: defaultFields(RECEIPT_FIELD_META)
      },
      feieForm: {
        title: '新订单',
        print_enabled: true,
        store_phone: '',
        store_address: '',
        show_specs: true,
        fields: defaultFields(FEIE_FIELD_META)
      },
      uploadItems: [
        { kind: 'logo', label: '店铺 Logo', pathKey: 'logo_path' },
        { kind: 'service_qr', label: '店铺客服（图四）', pathKey: 'service_qr_path' },
        { kind: 'mini_program_qr', label: '点餐小程序（图五）', pathKey: 'mini_program_qr_path' }
      ]
    }
  },
  computed: {
    printerLabel() {
      return this.activePrinter === 'feie' ? '飞鹅云模板' : '映美云模板'
    },
    jolimarkFieldRows() {
      return RECEIPT_FIELD_META
    },
    feieFieldRows() {
      return FEIE_FIELD_META
    },
    jolimarkStatusText() {
      const s = this.printerStatus && this.printerStatus.jolimark
      const base =
        'Logo 与二维码需公网可访问；请在服务器 .env 配置 JOLIMARK_APP_ID / JOLIMARK_APP_KEY / JOLIMARK_DEVICE_ID 与 RECEIPT_PUBLIC_BASE_URL。'
      if (!s) return base
      const on = s.print_enabled !== false
      const cfg = s.configured ? `设备 ${s.device_id || '****'} 已配置` : '映美云凭证未配置'
      return `${cfg}。打印功能${on ? '已开启' : '已关闭'}。${base}`
    },
    feieStatusText() {
      const s = this.printerStatus && this.printerStatus.feie
      if (!s) {
        return '请在服务器 .env 配置 FEIE_PRINT_USER / FEIE_PRINT_UKEY / FEIE_PRINT_SN。非随心订进单后自动出单。'
      }
      const on = s.print_enabled !== false
      const auto = s.auto_print ? '进单自动出单有效' : '进单不会自动出单'
      return s.configured
        ? `飞鹅已配置（SN ${s.sn || '****'}）。打印功能${on ? '已开启' : '已关闭'}（${auto}）。`
        : '飞鹅未配置：请在 .env 填写 FEIE_PRINT_USER / FEIE_PRINT_UKEY / FEIE_PRINT_SN。'
    }
  },
  mounted() {
    this.loadConfig()
    this.loadPrinterStatus()
  },
  methods: {
    onPrinterTab() {},
    assetUrl(pathKey) {
      const p = this.jolimarkForm[pathKey]
      if (!p) return ''
      if (/^https?:\/\//i.test(p)) return p
      return `${window.location.origin}${p.startsWith('/') ? p : `/${p}`}`
    },
    mergeFields(serverFields, meta) {
      const base = defaultFields(meta)
      const src = serverFields && typeof serverFields === 'object' ? serverFields : {}
      for (const key of Object.keys(base)) {
        const row = src[key]
        if (row && typeof row === 'object') {
          base[key] = {
            label: row.label != null ? String(row.label) : base[key].label,
            enabled: row.enabled !== false
          }
        }
      }
      return base
    },
    async loadPrinterStatus() {
      try {
        const resp = await this.$api.get('/admin/receipt-config/printer-status')
        if (resp && resp.code === 200) this.printerStatus = resp.data
      } catch (_e) {
        /* ignore */
      }
    },
    async loadConfig() {
      try {
        const resp = await this.$api.get('/admin/receipt-config')
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data
          this.jolimarkForm = {
            ...this.jolimarkForm,
            title: d.title || this.jolimarkForm.title,
            print_enabled: d.print_enabled !== false,
            logo_path: d.logo_path || this.jolimarkForm.logo_path,
            service_qr_path: d.service_qr_path || this.jolimarkForm.service_qr_path,
            mini_program_qr_path: d.mini_program_qr_path || this.jolimarkForm.mini_program_qr_path,
            service_qr_label: d.service_qr_label || this.jolimarkForm.service_qr_label,
            mini_program_qr_label: d.mini_program_qr_label || this.jolimarkForm.mini_program_qr_label,
            store_phone: d.store_phone || '',
            store_address: d.store_address || '',
            fields: this.mergeFields(d.fields, RECEIPT_FIELD_META)
          }
          const fe = d.feie || {}
          this.feieForm = {
            title: fe.title || '新订单',
            print_enabled: fe.print_enabled !== false,
            store_phone: fe.store_phone || d.store_phone || '',
            store_address: fe.store_address || d.store_address || '',
            show_specs: fe.show_specs !== false,
            fields: this.mergeFields(fe.fields, FEIE_FIELD_META)
          }
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载小票配置失败'))
      }
    },
    async savePrintEnabled(printer) {
      this.printSwitchSaving = true
      try {
        if (printer === 'feie') {
          const resp = await this.$api.put('/admin/receipt-config?printer=feie', {
            printer: 'feie',
            feie: { print_enabled: this.feieForm.print_enabled }
          })
          if (resp && resp.code === 200) {
            this.$message.success(
              this.feieForm.print_enabled ? '飞鹅云打印已开启' : '飞鹅云打印已关闭'
            )
            await this.loadPrinterStatus()
          }
        } else {
          const resp = await this.$api.put('/admin/receipt-config?printer=jolimark', {
            print_enabled: this.jolimarkForm.print_enabled
          })
          if (resp && resp.code === 200) {
            this.$message.success(
              this.jolimarkForm.print_enabled ? '映美云打印已开启' : '映美云打印已关闭'
            )
            await this.loadPrinterStatus()
          }
        }
      } catch (e) {
        if (printer === 'feie') {
          this.feieForm.print_enabled = !this.feieForm.print_enabled
        } else {
          this.jolimarkForm.print_enabled = !this.jolimarkForm.print_enabled
        }
        this.$message.error(formatApiError(e, '打印开关保存失败'))
      } finally {
        this.printSwitchSaving = false
      }
    },
    async saveConfig() {
      this.saving = true
      try {
        if (this.activePrinter === 'feie') {
          const resp = await this.$api.put('/admin/receipt-config?printer=feie', {
            printer: 'feie',
            feie: { ...this.feieForm }
          })
          if (resp && resp.code === 200) {
            this.$message.success(resp.message || '飞鹅云模板已保存')
            if (resp.data && resp.data.feie) {
              this.feieForm.print_enabled = resp.data.feie.print_enabled !== false
              this.feieForm.fields = this.mergeFields(resp.data.feie.fields, FEIE_FIELD_META)
            }
            await this.loadPrinterStatus()
          }
        } else {
          const resp = await this.$api.put('/admin/receipt-config?printer=jolimark', {
            ...this.jolimarkForm
          })
          if (resp && resp.code === 200) {
            this.$message.success(resp.message || '映美云模板已保存')
            if (resp.data) {
              this.jolimarkForm.print_enabled = resp.data.print_enabled !== false
              this.jolimarkForm.fields = this.mergeFields(resp.data.fields, RECEIPT_FIELD_META)
            }
            await this.loadPrinterStatus()
          }
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.saving = false
      }
    },
    async uploadAsset(opt, kind) {
      const fd = new FormData()
      fd.append('file', opt.file)
      try {
        const resp = await this.$api.post(`/admin/receipt-config/upload?kind=${encodeURIComponent(kind)}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          const map = {
            logo: 'logo_path',
            service_qr: 'service_qr_path',
            mini_program_qr: 'mini_program_qr_path'
          }
          const key = map[kind]
          if (key) this.jolimarkForm[key] = resp.data.url
          this.$message.success('图片已上传')
          if (typeof opt.onSuccess === 'function') opt.onSuccess(resp)
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '上传失败'))
        if (typeof opt.onError === 'function') opt.onError(e)
      }
    },
    openPreview() {
      const token = getAdminToken()
      const base = (this.$api && this.$api.defaults && this.$api.defaults.baseURL) || '/api'
      const printer = this.activePrinter === 'feie' ? 'feie' : 'jolimark'
      const url = `${base.replace(/\/$/, '')}/admin/receipt-config/preview?printer=${printer}&token=${encodeURIComponent(token || '')}`
      window.open(url, '_blank')
    }
  }
}
</script>

<style scoped>
.receipt-settings-page {
  padding: 0 4px 24px;
}
.receipt-page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}
.receipt-page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  font-family: var(--app-font);
}
.receipt-page-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: #64748b;
}
.header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.printer-tabs {
  margin-bottom: 8px;
}
.receipt-alert {
  margin-bottom: 16px;
}
.print-switch-panel {
  margin-bottom: 16px;
}
.print-switch-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin: 12px 0 8px;
}
.print-switch-hint {
  font-size: 13px;
  color: #64748b;
}
.receipt-flat-grid {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.receipt-panel {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  padding: 18px 20px 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 28px rgba(15, 23, 42, 0.06);
}
.receipt-panel-full {
  width: 100%;
}
.panel-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.panel-head p,
.section-hint {
  margin: 6px 0 0;
  color: #909399;
  font-size: 13px;
}
.upload-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}
.upload-card {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 12px;
  text-align: center;
  background: #fafafa;
}
.upload-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}
.upload-preview {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}
.upload-preview img {
  max-width: 100%;
  max-height: 108px;
  object-fit: contain;
}
.upload-empty {
  color: #c0c4cc;
  font-size: 12px;
}
.fields-table {
  margin-top: 12px;
}
.feie-tips {
  margin: 12px 0 16px;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.8;
}
@media (max-width: 1100px) {
  .receipt-flat-grid {
    grid-template-columns: 1fr;
  }
  .upload-grid {
    grid-template-columns: 1fr;
  }
}
</style>
